// src/Payroll_Management/PayRoll_Report/EmployeeSalarySlipPrintIndv.jsx

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Printer, X } from "lucide-react";

const BASE_URL = "https://nissi-india.com";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const safeStr = (v) => ((v ?? "") + "").trim();
const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmt2 = (v) =>
  safeNum(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/* ✅ Indian number to words */
function numberToWordsIN(num) {
  num = Math.floor(safeNum(num));
  if (num === 0) return "Zero";

  const a = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven",
    "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen",
    "Fourteen", "Fifteen", "Sixteen", "Seventeen",
    "Eighteen", "Nineteen",
  ];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const two = (n) =>
    n < 20 ? a[n] : b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");

  const three = (n) =>
    (Math.floor(n / 100)
      ? a[Math.floor(n / 100)] + " Hundred "
      : "") + two(n % 100);

  const parts = [];
  const crore = Math.floor(num / 10000000);
  num %= 10000000;
  const lakh = Math.floor(num / 100000);
  num %= 100000;
  const thousand = Math.floor(num / 1000);
  num %= 1000;

  if (crore) parts.push(three(crore) + " Crore");
  if (lakh) parts.push(three(lakh) + " Lakh");
  if (thousand) parts.push(three(thousand) + " Thousand");
  if (num) parts.push(three(num));

  return parts.join(" ").trim();
}

const EmployeeSalarySlipPrintIndv = () => {
  const q = useQuery();

  const empId = q.get("empId") || q.get("empld") || "";
  const year = q.get("year") || "";
  const monthName = q.get("month") || q.get("monthName") || "";
  const finYearId =
    q.get("finYearId") ||
    localStorage.getItem("finYearId") ||
    "";

  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSlip = async () => {
    if (!finYearId || !empId || !year || !monthName) {
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(
        `${BASE_URL}/api/employee-salary-slip/indv`,
        {
          params: {
            finYearId: Number(finYearId),
            empId: Number(empId),
            year: Number(year),
            monthName: monthName,
          },
        }
      );

      if (res?.data?.success) {
        setRow(res.data.row || null);
      } else {
        setRow(null);
      }
    } catch {
      setRow(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlip();
    // eslint-disable-next-line
  }, []);

  const netPay = safeNum(row?.fld_Netpay);
  const words = `${numberToWordsIN(netPay)} Rupees Only`;

  return (
    <div className="bg-gray-100 min-h-screen p-4">

      {/* Buttons */}
      <div className="no-print max-w-4xl mx-auto flex justify-end gap-3 mb-4">
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 shadow"
        >
          <Printer size={16} />
          Print
        </button>

        <button
          onClick={() => window.close()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-700 text-white font-bold hover:bg-gray-800 shadow"
        >
          <X size={16} />
          Close
        </button>
      </div>

      <div className="sheet max-w-4xl mx-auto bg-white border border-black p-3 text-[11px]">

        {loading ? (
          <div className="text-center py-10">Loading...</div>
        ) : !row ? (
          <div className="text-center py-10">No Records Found</div>
        ) : (
          <>
            {/* ===== STATIC HEADER ===== */}
            <div className="border border-black">
              <div className="grid grid-cols-[180px_1fr] border-b border-black">
                <div className="p-2 border-r border-black flex items-center justify-center">
                  <img
                    src="/logo.png"
                    alt="logo"
                    style={{ width: 140, height: 70, objectFit: "contain" }}
                  />
                </div>
                <div className="p-2 text-center">
                  <div className="text-xl font-extrabold">Nissi Infotech</div>
                  <div className="font-semibold">CIVIL CONSTRUCTION</div>
                  <div className="text-xs mt-1">
                    3/42-12, Nagappa colony, kavundampalayam - Tamil Nadu
                  </div>
                  <div className="text-xs">
                    Phone No: 9003744644
                  </div>
                </div>
              </div>

              <div className="text-center font-extrabold py-2 border-b border-black">
                SALARY SLIP
              </div>

              <div className="text-center font-bold text-xs py-2 border-b border-black">
                SALARY PAY SLIP FOR THE MONTH OF {monthName} - {year}
              </div>

              {/* ===== EMPLOYEE DETAILS ===== */}
              <table className="w-full border-collapse">
                <tbody>
                  {[
                    ["EMPLOYEE CODE", row.fld_employeecode, "FATHER NAME", row.fld_fathername],
                    ["EMPLOYEE NAME", row.fld_employee, "AADHAR NO", row.fld_Aadharcardnumber],
                    ["DESIGNATION", row.fld_designationname, "INCOME TAX NO(PAN)", row.fld_PANNo],
                    ["GENDER", row.fld_gender, "BANK NAME", row.fld_bankname],
                    ["DATE OF BIRTH", row.DOB, "BANK A/C NO", row.fld_accountno],
                    ["DATE OF JOIN", row.DOJ, "PF NO", row.fld_PFNo],
                    ["DEPARTMENT", row.fld_departmentname, "ESI NO", row.fld_ESINo],
                    ["MOBILE NO", row.fld_mobile, "COUNTRY", row.fld_countryname],
                    ["MARITAL STATUS", row.fld_maritalstatus, "STATE", row.fld_statename],
                    ["QUALIFICATION", row.fld_qualificationname, "DISTRICT", row.fld_districtname],
                    ["ADDRESS", row.fld_permaddress, "CITY", row.fld_cityname],
                    ["", "", "LOCATION", row.fld_branchname],
                    ["", "", "SALARY BASIS", row.fld_salarybasisname],
                  ].map((r, i) => (
                    <tr key={i} className="border-t border-black">
                      <td className="border-r border-black p-2 font-bold w-[160px]">{r[0]}</td>
                      <td className="border-r border-black p-2 w-[220px]">{safeStr(r[1])}</td>
                      <td className="border-r border-black p-2 font-bold w-[160px]">{r[2]}</td>
                      <td className="p-2">{safeStr(r[3])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* ===== EARNINGS / DEDUCTIONS ===== */}
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-t border-black">
                    <th className="border-r border-black p-2 text-left">EARNINGS</th>
                    <th className="border-r border-black p-2 text-right">AMOUNT</th>
                    <th className="border-r border-black p-2 text-left">DEDUCTIONS</th>
                    <th className="p-2 text-right">AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["BASIC PAY", row.fld_basicpay, "EMPLOYEE PF", row.fld_pf],
                    ["PAID BASIC", row.fld_paidbasic, "ESI", row.fld_esi],
                    ["CONVEYANCE", row.fld_conveyance, "TDS", row.fld_tds],
                    ["EDUCATIONAL", row.fld_educational, "LOP AMOUNT", row.fld_lopamt],
                    ["MEDICAL", row.fld_Medical, "ADVANCE DEDUCTION", row.fld_advance],
                    ["HRA", row.fld_hra, "LOAN DEDUCTION", row.loandetection],
                    ["GROSS SALARY", row.fld_GrossSalary, "", ""],
                  ].map((x, i) => (
                    <tr key={i} className="border-t border-black">
                      <td className="border-r border-black p-2 font-bold">{x[0]}</td>
                      <td className="border-r border-black p-2 text-right">{fmt2(x[1])}</td>
                      <td className="border-r border-black p-2 font-bold">{x[2]}</td>
                      <td className="p-2 text-right">{x[2] ? fmt2(x[3]) : ""}</td>
                    </tr>
                  ))}

                  <tr className="border-t border-black">
                    <td colSpan={2} className="border-r border-black p-2 font-bold">
                      Rs. {words}
                    </td>
                    <td className="border-r border-black p-2 font-extrabold">
                      NET PAY RS
                    </td>
                    <td className="p-2 text-right font-extrabold">
                      {fmt2(netPay)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <style>{`
        @page { size: A4 portrait; margin: 10mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .sheet { border: none !important; }
        }
      `}</style>
    </div>
  );
};

export default EmployeeSalarySlipPrintIndv;